import { useEffect, useState } from 'react';
import { VIDEO_CONFIG } from '../config/videoConfig.js';

export interface SpeakingIndicatorOptions {
  /** Level (0..1) at or above which the participant counts as speaking */
  threshold: number;
  /** How long the indicator stays on after the level drops below threshold */
  holdMs: number;
}

const DEFAULT_OPTIONS: SpeakingIndicatorOptions = {
  threshold: VIDEO_CONFIG.speaking.threshold,
  holdMs: VIDEO_CONFIG.speaking.holdMs
};

/**
 * Turns a raw audio level into a steady "is speaking" flag.
 *
 * Attack is immediate: the flag is derived during render, so it is true on
 * the very render in which `level` crosses the threshold, with no timer in
 * between. Release is held: once the level drops, the flag stays on for
 * `holdMs` and clears only if the level stays low for the whole window, so
 * the ring does not blink off between syllables.
 *
 * The effect below only re-runs when the level crosses the threshold, not on
 * every sample, so a 30 Hz level stream schedules at most one timer per
 * pause.
 */
export function useSpeakingIndicator(
  level: number,
  { threshold, holdMs }: SpeakingIndicatorOptions = DEFAULT_OPTIONS
): boolean {
  const isLoud = level >= threshold;
  const [isHolding, setIsHolding] = useState(false);

  useEffect(() => {
    if (isLoud) {
      setIsHolding(true);
      return undefined;
    }
    if (!isHolding) return undefined;

    const release = setTimeout(() => setIsHolding(false), holdMs);
    return () => clearTimeout(release);
  }, [isLoud, isHolding, holdMs]);

  return isLoud || isHolding;
}
